import { Counter, Tab, Box } from '@cad-arc';
import { BoloPanelFormType } from '@mark43/cad-api';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { FieldPath, useFormState } from 'react-hook-form';
import {
    useActiveBoloPanelTab,
    useSetActiveBoloPanelTab,
} from '../../state/active-bolo-panel-tab-provider';
import { BoloPanelTab } from '../../config/bolo-panel-tabs-config';
import { useIntegratedForm } from '../../../../forms/use-integrated-form';
import { Tooltip } from '~/common/components/tooltip';
import styles from './bolo-panel-tabs.module.css';

export const BoloPanelTabHeader = observer(function TabHeader(props: {
    value: BoloPanelTab;
    leadingVisual: typeof Tab['leadingVisual'];
    onSelect?: () => void;
    tooltip: string;
    errorFields?: FieldPath<BoloPanelFormType>[];
    tabHeaderWidth: string;
}): React.ReactElement {
    const activeTab = useActiveBoloPanelTab();
    const setActiveTab = useSetActiveBoloPanelTab();
    const boloForm = useIntegratedForm<BoloPanelFormType>().form;
    const { errors } = useFormState({
        control: boloForm.control,
        name: props.errorFields ?? [],
        disabled: !props.errorFields || props.errorFields.length === 0,
    });
    const errorCount = (props.errorFields ?? []).reduce((errorCount, errorField) => {
        let localCount = errorCount;
        const errorsForField = errors[errorField as keyof typeof errors];
        if (Array.isArray(errorsForField)) {
            errorsForField.forEach((errorObject) => {
                if (errorObject) {
                    localCount = localCount + Object.keys(errorObject).length;
                }
            });
        }
        return localCount;
    }, 0);

    const handleClick = useCallback(() => {
        setActiveTab(activeTab === props.value ? undefined : props.value);
    }, [activeTab, props.value, setActiveTab]);

    return (
        <Tooltip overlay={props.tooltip}>
            <Box position="relative" minWidth={props.tabHeaderWidth}>
                {errorCount > 0 ? <Counter variant="important">{errorCount}</Counter> : undefined}
                <Tab
                    data-testid={props.value}
                    value={props.value}
                    className={styles.tab}
                    onClick={handleClick}
                    leadingVisual={props.leadingVisual}
                />
            </Box>
        </Tooltip>
    );
});
